import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PulseBar } from '@/features/profile/PulseBar';
import { radius } from '@/theme';

import { useTownPulse } from './useDiscovery';

interface TownPulseCardProps {
  country: string;
  town: string;
}

/**
 * Spec §8.1. Shown above the feed when a place is selected — the thing that
 * makes the country filter feel like somewhere rather than a dropdown value.
 */
export function TownPulseCard({ country, town }: TownPulseCardProps) {
  const { t } = useTranslation();
  const params: Record<string, string> = {};
  if (country) params.country = country;
  if (country && town) params.town = town;

  const pulse = useTownPulse(params);

  if (pulse.isPending) {
    return (
      <Card sx={{ p: 2 }} aria-busy="true" aria-label={t('pulse.loading')}>
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="45%" height={18} />
          <Skeleton variant="rounded" height={10} />
          <Skeleton variant="text" width="80%" height={14} />
        </Stack>
      </Card>
    );
  }

  if (pulse.isError) return null;

  const data = pulse.data;

  return (
    <Card sx={{ p: 2 }} component="section" aria-label={t('pulse.for', { place: data.place })}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          {data.countryFlag && <span aria-hidden>{data.countryFlag}</span>}
          <Typography variant="overline" color="text.secondary">
            {data.place}
          </Typography>
        </Stack>

        {/* The percentages come from the API, already nudged to add up to
            100; the sentence around them is written here, in the reader's
            language. */}
        <PulseBar
          pulse={data.pulse}
          subject={t('pulse.thisWeek', { place: data.place })}
          percentages={data.percentages}
          emptyText={t('pulse.emptyFor', { place: data.place })}
          showLegend={false}
        />

        <Typography variant="caption" color="text.secondary">
          {t('pulse.postsThisWeek', { count: data.postsThisWeek })}
        </Typography>

        {data.topIdeal && (
          <>
            <Divider />
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AutoAwesomeRoundedIcon sx={{ fontSize: 14, color: 'ideal.text' }} aria-hidden />
                <Typography variant="caption" sx={{ color: 'ideal.text', fontWeight: 600 }}>
                  {t('pulse.topIdeal')}
                </Typography>
              </Stack>
              <Box
                component={RouterLink}
                to={`/posts/${data.topIdeal.id}`}
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  color: 'inherit',
                  p: 1,
                  borderRadius: `${radius.sm}px`,
                  border: 1,
                  borderColor: (theme) => theme.palette.ideal.border,
                  backgroundColor: (theme) => theme.palette.ideal.softer,
                  '&:hover': { backgroundColor: (theme) => theme.palette.ideal.soft },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {data.topIdeal.title ?? data.topIdeal.body}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('vote.count', { count: data.topIdeal.upvoteCount })}
                  {data.topIdeal.authorUsername
                    ? ` · @${data.topIdeal.authorUsername}`
                    : ` · ${t('post.anonymous').toLocaleLowerCase()}`}
                </Typography>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}
